import { AxiosError, AxiosResponse, AxiosInstance } from 'axios'

export type TokensPair = {
  accessToken: string
  refreshToken: string
}

export type MakeRefreshTokenAndRetryProps = {
  getRefreshToken: () => string | null | undefined
  refreshTokenPair: (refreshToken: string) => Promise<TokensPair>
  saveRefreshToken: (refreshToken: string) => void
  saveAccessToken: (accessToken: string) => void
  clearAccessToken: () => void
  clearRefreshToken: () => void
  onFailedRetry?: (error: any) => void
  client: AxiosInstance
}

export const makeRefreshTokenAndRetry = ({
  getRefreshToken,
  refreshTokenPair,
  saveRefreshToken,
  saveAccessToken,
  clearAccessToken,
  clearRefreshToken,
  onFailedRetry,
  client,
}: MakeRefreshTokenAndRetryProps) => {
  let invalidToken: string | null = null
  let tokenPromise: Promise<TokensPair> | null = null

  const retryRequest = (errorResponse: AxiosResponse<any>, accessToken: string) => {
    const config = { ...errorResponse.config }
    config.headers.Authorization = `Token ${accessToken}`

    return client(errorResponse.config)
  }

  return async (error: AxiosError) => {
    const { response: errorResponse } = error
    const refreshToken = getRefreshToken()

    if (!refreshToken || !errorResponse?.config || !errorResponse.config.headers.Authorization) throw error

    const currentToken = errorResponse?.config.headers.Authorization.split(' ')[1]

    if (tokenPromise && invalidToken === currentToken)
      try {
        const response = await tokenPromise
        return retryRequest(errorResponse, response.accessToken)
      } catch (e) {
        throw e
      }

    if (!invalidToken || invalidToken !== currentToken)
      try {
        invalidToken = currentToken

        tokenPromise = refreshTokenPair(refreshToken)
        const response = await tokenPromise
        saveRefreshToken(response.refreshToken)
        saveAccessToken(response.accessToken)

        return retryRequest(errorResponse, response.accessToken)
      } catch (e) {
        tokenPromise = null
        invalidToken = null
        clearAccessToken()
        clearRefreshToken()
        onFailedRetry?.(e)
        throw e
      }

    throw error
  }
}
